import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  InputAdornment,
  TextField,
  TablePagination,
} from "@mui/material";
import { CirclePlus, Search, Upload } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import {
  getDukas,
  createDuka,
  updateDuka,
  deleteDuka,
  bulkUploadDukaMapping
} from "../../api/Duka/api";
import { getUsers } from "../../api/Authentication/api";
import usePermissions from "./hooks/usePermissions";
import DataTable from "../../components/DataTable";
import DukaForm from "../../components/shared/forms/DukaForm";
import { AuthContext } from "../../contexts/AuthContext";
import Toast from "../../components/shared/Toast";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import UploadModal from "../../components/UploadModal";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

const DukaList = () => {
  const {
    loading: loadingPermissions,
    error: permissionsError,
    can,
  } = usePermissions();
  const { user } = useContext(AuthContext);
  const [dukas, setDukas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDuka, setSelectedDuka] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dukaToDelete, setDukaToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    if (!loadingPermissions && can.viewDuka()) {
      loadUsers();
      fetchDukas();
    }
  }, [loadingPermissions]);

  const loadUsers = async () => {
    try {
      const userData = await getUsers();
      setUsers(userData);
      console.log("Users loaded:", userData);
    } catch (err) {
      console.error("Error loading users:", err);
      setError("Failed to load users. Some features might be limited.");
    }
  };

  const fetchDukas = async () => {
    setLoading(true);
    try {
      const response = await getDukas({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });
      if (response?.data) {
        const transformedData = response.data.map((duka) => {
          // Get coordinator names from the coordinators array
          const coordinatorNames = duka.coordinators
            .map((coord) => coord.user)
            .join(", ");

          return {
            id: duka.id,
            name: duka.name,
            location: duka.location,
            contactPerson: duka.contact_person,
            phoneNumber: duka.phone_number,
            email: duka.email,
            code: duka.code,
            client: duka.client,
            clientName: duka.client || "N/A",
            coordinatorNames: coordinatorNames || "N/A",
          };
        });
        console.log("Dukas loaded:", transformedData);
        setDukas(transformedData);
        setTotal(response.total);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching dukas:", err);
      setError("Failed to load dukas. Please try again later.");
      setDukas([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDukas();
  }, [page, rowsPerPage, searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const transformDukaData = (data) => {
    // Remove clientName and coordinatorNames as they are only for display
    const { clientName, coordinatorNames, ...dukaData } = data;

    return {
      name: dukaData.name,
      location: dukaData.location,
      contact_person: dukaData.contactPerson,
      phone_number: dukaData.phoneNumber,
      email: dukaData.email,
      client: dukaData.client,
      duka_coordinators: dukaData.dukaCoordinators,
    };
  };

  const handleCreateDuka = async (data) => {
    try {
      const transformedData = transformDukaData(data);
      console.log("transform Data", transformedData);

      await createDuka(transformedData);
      setOpenModal(false);
      fetchDukas();
      showSuccessToast("Duka created successfully");
    } catch (error) {
      console.error("Error creating duka:", error);
      setError("Failed to create duka. Please try again.");
      showErrorToast("Failed to create duka. Please try again.");
    }
  };

  const handleUpdateDuka = async (id, data) => {
    try {
      const transformedData = transformDukaData(data);
      await updateDuka(id, transformedData);
      setOpenModal(false);
      setSelectedDuka(null);
      fetchDukas();
      showSuccessToast("Duka updated successfully");
    } catch (error) {
      console.error("Error updating duka:", error);
      setError("Failed to update duka. Please try again.");
      showErrorToast("Failed to update duka. Please try again.");
    }
  };

  const handleDeleteDuka = async () => {
    if (!dukaToDelete) return;

    try {
      await deleteDuka(dukaToDelete.id);
      setDeleteModalOpen(false);
      setDukaToDelete(null);
      fetchDukas();
      showSuccessToast("Duka deleted successfully");
    } catch (error) {
      console.error("Error deleting duka:", error);
      setError("Failed to delete duka. Please try again.");
      showErrorToast("Failed to delete duka. Please try again.");
    }
  };

  const handleSubmit = (data) => {
    console.log("data", data);

    if (selectedDuka) {
      handleUpdateDuka(selectedDuka.id, data);
    } else {
      handleCreateDuka(data);
    }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
  };

  const handleCSVUpload = async () => {
    if (!uploadFile) {
      showErrorToast("Please select a CSV file", "Validation Error");
      return;
    }
    try {
      await bulkUploadDukaMapping(uploadFile);
      showSuccessToast("Bulk upload successful");
      setOpenUploadModal(false);
      fetchDukas();
    } catch (err) {
      showErrorToast("Bulk upload failed", "Error");
    }
  };

  const filteredDukas = dukas.filter((duka) =>
    Object.values(duka).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const columns = [
    { id: "code", label: "Code" },
    { id: "name", label: "Name" },
    { id: "location", label: "Location" },
    { id: "contactPerson", label: "Contact Person" },
    { id: "phoneNumber", label: "Phone Number" },
    { id: "email", label: "Email" },
    { id: "clientName", label: "Client" },
    { id: "coordinatorNames", label: "Coordinators" },
  ];

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Manage Duka
      </Typography>

      {(error || permissionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || permissionsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          {can.createDuka() && (
            <Button
              variant="contained"
              startIcon={<Upload size={18} />}
              onClick={() => setOpenUploadModal(true)}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": {
                  backgroundColor: "#1a614d !important",
                },
              }}
            >
              Upload CSV
            </Button>
          )}
          {can.createDuka() && (
            <Button
              variant="contained"
              startIcon={<CirclePlus size={18} />}
              onClick={() => {
                setSelectedDuka(null);
                setOpenModal(true);
              }}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": {
                  backgroundColor: "#1a614d !important",
                },
              }}
            >
              Add Duka
            </Button>
          )}
        </Box>
      </Box>

      <DataTable
        loading={loading || loadingPermissions}
        columns={columns}
        rows={filteredDukas}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onEdit={
          can.editDuka()
            ? (rowData) => {
                setSelectedDuka(rowData);
                setOpenModal(true);
              }
            : undefined
        }
        onDelete={
          can.deleteDuka()
            ? (rowData) => {
                setDukaToDelete(rowData);
                setDeleteModalOpen(true);
              }
            : undefined
        }
        editIcon={<EditIcon />}
        deleteIcon={<DeleteIcon />}
        editTooltip="Edit"
        deleteTooltip="Delete"
        editDisabled={!can.editDuka()}
        deleteDisabled={!can.deleteDuka()}
        totalRows={total}
        components={{
          Pagination: () => (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ),
        }}
      />

      {(can.createDuka() || can.editDuka()) && (
        <Modal
          open={openModal}
          onClose={() => {
            setSelectedDuka(null);
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <DukaForm
              initialData={selectedDuka}
              onSubmit={handleSubmit}
              onCancel={() => {
                setSelectedDuka(null);
                setOpenModal(false);
              }}
            />
          </Box>
        </Modal>
      )}

      <UploadModal
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        onFileChange={handleFileChange}
        onUpload={handleCSVUpload}
      />

      {can.deleteDuka() && (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setDukaToDelete(null);
          }}
          onConfirm={handleDeleteDuka}
          title="Delete Duka"
          message="Are you sure you want to delete this duka?"
          itemName={dukaToDelete?.name}
        />
      )}
      <Toast />
    </Box>
  );
};

export default DukaList;
